import React from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/posthog";

type Props = {
  onReset?: () => void;
  onAutoRecover?: () => void;
};

type State = { 
  hasError: boolean; 
  error?: Error;
  errorCount: number;
  isRecovering: boolean;
};

export class TourErrorBoundary extends React.Component<React.PropsWithChildren<Props>, State> {
  state: State = { hasError: false, errorCount: 0, isRecovering: false };
  private recoveryTimeout?: NodeJS.Timeout;

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('TourErrorBoundary caught error:', error, info);
    
    const newErrorCount = this.state.errorCount + 1;
    this.setState({ errorCount: newErrorCount });

    // Track error in analytics
    trackEvent('tour_error_caught', {
      error_message: error?.message,
      error_stack: error?.stack,
      component_stack: info?.componentStack,
      error_count: newErrorCount,
    });
    
    // Log to any error tracking service
    const w = window as any;
    if (Array.isArray(w.__BOOT_ERRORS)) {
      w.__BOOT_ERRORS.push({ 
        type: 'tour_error', 
        message: error?.message, 
        stack: info?.componentStack 
      });
    }

    // Auto-recover after 2 errors on the same step
    if (newErrorCount >= 2 && this.props.onAutoRecover) {
      this.setState({ isRecovering: true });
      
      trackEvent('tour_auto_recovery_triggered', {
        error_count: newErrorCount,
      });

      // Give user a moment to see the recovery message
      this.recoveryTimeout = setTimeout(() => {
        this.props.onAutoRecover?.();
        this.setState({ 
          hasError: false, 
          error: undefined, 
          errorCount: 0,
          isRecovering: false 
        });
      }, 1500);
    }
  }

  componentWillUnmount(): void {
    if (this.recoveryTimeout) {
      clearTimeout(this.recoveryTimeout);
    }
  }

  private handleReset = () => {
    trackEvent('tour_error_reset');
    
    // Clear tour progress
    localStorage.removeItem("hasSeenOnboardingTour");
    localStorage.removeItem("onboardingTourStep");
    
    this.setState({ hasError: false, error: undefined, errorCount: 0 });
    this.props.onReset?.();
  };

  private handleSkip = () => {
    trackEvent('tour_error_skip');
    
    // Mark tour as seen to prevent it from showing again
    localStorage.setItem("hasSeenOnboardingTour", "true");
    localStorage.removeItem("onboardingTourStep");
    
    this.setState({ hasError: false, error: undefined, errorCount: 0 });
  };

  render() {
    if (this.state.hasError) {
      // Show recovery message if auto-recovering
      if (this.state.isRecovering) {
        return (
          <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100]" />

            {/* Recovery Card */}
            <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
              <div className="pointer-events-auto w-full max-w-lg">
                <Card className="shadow-2xl border-primary/50">
                  <CardContent className="pt-6 text-center space-y-4">
                    <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
                    <div className="space-y-2">
                      <h3 className="font-semibold text-lg">Recovering...</h3>
                      <p className="text-sm text-muted-foreground">
                        Skipping problematic step and continuing tour
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        );
      }

      return (
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100]" />

          {/* Error Card */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
            <div className="pointer-events-auto w-full max-w-lg">
              <Card className="shadow-2xl border-destructive/50">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-destructive shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <CardTitle className="text-xl">Tour Interrupted</CardTitle>
                      <CardDescription>
                        The onboarding tour encountered an error and couldn't continue.
                        {this.state.errorCount >= 2 && " Attempting automatic recovery..."}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
                    <p className="text-sm text-muted-foreground">
                      Error: {this.state.error?.message || "Unknown error occurred"}
                    </p>
                    {this.state.errorCount > 1 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Error count: {this.state.errorCount}
                      </p>
                    )}
                  </div>
                </CardContent>

                <CardFooter className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    onClick={this.handleReset} 
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    Restart Tour
                  </Button>
                  <Button 
                    onClick={this.handleSkip}
                    className="w-full sm:w-auto"
                  >
                    Skip Tour
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </>
      );
    }

    return this.props.children;
  }
}
